import { cn } from "@/lib/utils";

const STATUS = {
  Compliant: "bg-emerald-50 text-emerald-700 border-emerald-200|bg-emerald-500",
  "Partially Compliant": "bg-amber-50 text-amber-700 border-amber-200|bg-amber-500",
  "Non-Compliant": "bg-rose-50 text-rose-700 border-rose-200|bg-rose-500",
  "Evidence Required": "bg-indigo-50 text-indigo-700 border-indigo-200|bg-indigo-500",
  "Future Effective": "bg-purple-50 text-purple-700 border-purple-200|bg-purple-500",
  "Requires Legal Review": "bg-sky-50 text-sky-700 border-sky-200|bg-sky-500",
  "Not Assessed": "bg-slate-50 text-slate-600 border-slate-200|bg-slate-400",
  "Not Applicable": "bg-slate-50 text-slate-500 border-slate-200|bg-slate-300",
  Effective: "bg-blue-50 text-blue-700 border-blue-200|bg-blue-600",
  Conditional: "bg-slate-50 text-slate-700 border-slate-300|bg-slate-500",
  Superseded: "bg-slate-50 text-slate-400 border-slate-200|bg-slate-300",
  Open: "bg-rose-50 text-rose-700 border-rose-200|bg-rose-500",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200|bg-blue-500",
  "In Progress": "bg-indigo-50 text-indigo-700 border-indigo-200|bg-indigo-500",
  "Pending Evidence": "bg-amber-50 text-amber-700 border-amber-200|bg-amber-500",
  "Pending Review": "bg-sky-50 text-sky-700 border-sky-200|bg-sky-500",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200|bg-emerald-500",
  Closed: "bg-slate-100 text-slate-700 border-slate-300|bg-slate-500",
  "Accepted Risk": "bg-orange-50 text-orange-700 border-orange-200|bg-orange-500",
  Verified: "bg-emerald-50 text-emerald-700 border-emerald-200|bg-emerald-500",
  Unverified: "bg-slate-50 text-slate-600 border-slate-200|bg-slate-400",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200|bg-rose-500",
};
const LABEL = { "Partially Compliant": "Partial", "Requires Legal Review": "Legal Review" };

export const StatusBadge = ({ status, testId, className }) => {
  const [cls, dot] = (STATUS[status] || STATUS["Not Assessed"]).split("|");
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium", cls, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {LABEL[status] || status || "—"}
    </span>
  );
};

const RISK = {
  Critical: "text-rose-700 bg-rose-100 border-rose-300",
  High: "text-orange-700 bg-orange-100 border-orange-300",
  Medium: "text-amber-700 bg-amber-100 border-amber-300",
  Low: "text-slate-700 bg-slate-100 border-slate-300",
  Informational: "text-slate-500 bg-white border-slate-200",
};
export const RiskBadge = ({ risk, testId }) => (
  <span data-testid={testId} className={cn("inline-flex rounded border px-1.5 py-0.5 text-[11px] font-semibold", RISK[risk] || RISK.Informational)}>{risk || "—"}</span>
);

const BASIS = {
  Law: "text-blue-800 bg-blue-50 border-blue-200",
  "Regulatory interpretation": "text-violet-800 bg-violet-50 border-violet-200",
  "Industry best practice": "text-teal-800 bg-teal-50 border-teal-200",
  "Product recommendation": "text-slate-700 bg-slate-50 border-slate-200",
};
export const BasisBadge = ({ basis }) => (
  <span className={cn("inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide", BASIS[basis] || BASIS["Product recommendation"])}>{basis}</span>
);

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      <h1 data-testid={testId || "page-title"} className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500 max-w-3xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Field = ({ label, children, mono }) => (
  <div>
    <div className="label-caps mb-1">{label}</div>
    <div className={cn("text-sm text-slate-800 whitespace-pre-wrap", mono && "font-mono text-xs")}>{children || "—"}</div>
  </div>
);

export const Panel = ({ title, children, className, actions, testId }) => (
  <section data-testid={testId} className={cn("rounded-lg border border-slate-200 bg-white", className)}>
    {title && (
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {actions}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Empty = ({ text }) => <div className="py-10 text-center text-sm text-slate-400">{text}</div>;
